import { useState, useRef, useEffect } from 'react'
import { Mic, Square, Volume2, Sparkles, Headphones } from 'lucide-react'

interface VoiceInterfaceProps {
  isActive: boolean
  onVoiceToggle: (active: boolean) => void
}

function VoiceInterface({ isActive, onVoiceToggle }: VoiceInterfaceProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [audioLevel, setAudioLevel] = useState(0)
  const [transcription, setTranscription] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const dataArrayRef = useRef<Uint8Array | null>(null)
  const levelIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isActive) {
      initializeAudioContext()
    } else {
      cleanupAudio()
    }
    return cleanupAudio
  }, [isActive])

  const initializeAudioContext = async () => {
    try {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
      analyserRef.current = audioContextRef.current.createAnalyser()
      analyserRef.current.fftSize = 256
      dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount)
    } catch (error) {
      console.error('Error initializing audio context:', error)
    }
  }

  const cleanupAudio = () => {
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current)
      levelIntervalRef.current = null
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
    }
    setAudioLevel(0)
  }

  const startRecording = async () => {
    if (!audioContextRef.current || !analyserRef.current || !dataArrayRef.current) {
      alert('Audio context not initialized')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      
      // Connect audio stream to analyser for level monitoring
      const source = audioContextRef.current.createMediaStreamSource(stream)
      source.connect(analyserRef.current)

      // Start audio level monitoring
      levelIntervalRef.current = setInterval(() => {
        if (analyserRef.current && dataArrayRef.current) {
          analyserRef.current.getByteFrequencyData(dataArrayRef.current)
          const average = dataArrayRef.current.reduce((a, b) => a + b) / dataArrayRef.current.length
          setAudioLevel(Math.min(100, (average / 255) * 100))
        }
      }, 100)

      // Setup media recorder for actual recording
      mediaRecorderRef.current = new MediaRecorder(stream)
      const chunks: Blob[] = []

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data)
        }
      }

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/wav' })
        await processAudio(audioBlob)
        
        // Cleanup stream
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorderRef.current.start()
      setIsRecording(true)
      setTranscription('')
      setAudioLevel(0)
    } catch (error) {
      console.error('Error starting recording:', error)
      alert('Error accessing microphone. Please check permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
    setAudioLevel(0)
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current)
      levelIntervalRef.current = null
    }
  }

  const processAudio = async (audioBlob: Blob) => {
    setIsProcessing(true)
    
    try {
      // Convert audio blob to base64
      const reader = new FileReader()
      reader.onloadend = async () => {
        const base64Audio = (reader.result as string).split(',')[1]
        
        try {
          // Send to enhanced backend for transcription
          const response = await fetch('/api/voice/transcribe', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              audio_data: base64Audio,
              session_id: 'voice_client_' + Date.now(),
              format: 'wav'
            }),
          })

          if (response.ok) {
            const result = await response.json()
            setTranscription(result.transcription || 'Audio processed successfully')
          } else {
            setTranscription('Transcription failed. Please try again.')
          }
        } catch (error) {
          console.error('Transcription error:', error)
          setTranscription('Error processing audio. Please check your connection.')
        } finally {
          setIsProcessing(false)
        }
      }
      reader.readAsDataURL(audioBlob)
    } catch (error) {
      console.error('Audio processing error:', error)
      setTranscription('Error processing audio.')
      setIsProcessing(false)
    }
  }

  const playResponse = async () => {
    if (!transcription) return
    
    try {
      // Get AI response for the transcription
      const response = await fetch('/api/voice/synthesize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `text=${encodeURIComponent(transcription)}&voice=default`
      })

      if (response.ok) {
        const result = await response.json()
        // In a real implementation, you would play the audio data
        // For now, we'll simulate with a text-to-speech fallback
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(transcription)
          utterance.rate = 0.9
          utterance.pitch = 1
          speechSynthesis.speak(utterance)
        } else {
          alert('Text-to-speech not supported in this browser')
        }
      }
    } catch (error) {
      console.error('Voice synthesis error:', error)
      // Fallback to browser TTS
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(transcription)
        utterance.rate = 0.9
        utterance.pitch = 1
        speechSynthesis.speak(utterance)
      }
    }
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-green-900/20 to-blue-900/20 p-6">
      {/* Status Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className={`w-4 h-4 rounded-full animate-pulse ${
            isActive ? 'bg-green-400' : 'bg-red-400'
          }`} />
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
            🎤 Voice Interface
          </h2>
        </div>
        <button
          onClick={() => onVoiceToggle(!isActive)}
          className={`px-6 py-3 rounded-xl font-medium transition-all transform hover:scale-105 ${
            isActive 
              ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white shadow-lg' 
              : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg'
          }`}
        >
          {isActive ? '🔴 Disable Voice' : '🟢 Enable Voice'}
        </button>
      </div>

      {!isActive ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-purple-300">
            <Headphones className="w-20 h-20 mx-auto mb-6 text-purple-500 animate-pulse" />
            <h3 className="text-2xl font-semibold mb-3 text-purple-200">Voice Interface Disabled</h3>
            <p className="text-purple-400 mb-4">Enable voice to start conversation with AI assistant</p>
            <div className="text-sm text-purple-500">
              <p>🎙️ Real-time voice recognition</p>
              <p>🗣️ Natural language processing</p>
              <p>🔊 AI voice responses</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Recording Controls */}
          <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-purple-300 flex items-center">
                <Mic className="w-6 h-6 mr-2" />
                Voice Controls
              </h3>
              <div className="flex items-center space-x-3">
                <span className={`w-3 h-3 rounded-full animate-pulse ${
                  isRecording ? 'bg-red-400' : 'bg-green-400'
                }`} />
                <span className="text-sm text-purple-400 font-medium">
                  {isProcessing ? '🧠 Processing...' : isRecording ? '🔴 Recording...' : '🟢 Ready'}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-medium transition-all transform hover:scale-105 ${
                  isProcessing
                    ? 'bg-gray-600 cursor-not-allowed'
                    : isRecording
                    ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white shadow-lg'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg'
                }`}
              >
                {isRecording ? (
                  <>
                    <Square className="w-5 h-5" />
                    <span>Stop Recording</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5" />
                    <span>Start Recording</span>
                  </>
                )}
              </button>
              
              <button
                onClick={playResponse}
                disabled={!transcription || isProcessing}
                className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg"
              >
                <Volume2 className="w-5 h-5" />
                <span>🔊 Play Response</span>
              </button>
            </div>
            
            {/* Audio Level Meter */}
            {isRecording && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-purple-400 font-medium">Audio Level:</span>
                  <span className="text-sm font-mono text-purple-300">{audioLevel}%</span>
                </div>
                <div className="w-full bg-black/30 backdrop-blur-md rounded-full h-3 border border-white/10">
                  <div 
                    className="bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 h-3 rounded-full transition-all duration-100 shadow-lg"
                    style={{ width: `${audioLevel}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-purple-500 mt-1">
                  <span>Quiet</span>
                  <span>Optimal</span>
                  <span>Loud</span>
                </div>
              </div>
            )}
          </div>

          {/* Transcription Area */}
          <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 flex-1 border border-white/10">
            <h3 className="text-xl font-semibold text-purple-300 mb-4 flex items-center">
              <Sparkles className="w-5 h-5 mr-2" />
              Voice Transcription
            </h3>
            {transcription ? (
              <div className="bg-black/30 backdrop-blur-md rounded-xl p-4 border border-purple-500/30">
                <p className="text-purple-200 leading-relaxed whitespace-pre-wrap">{transcription}</p>
                <div className="mt-4 flex items-center justify-between text-sm text-purple-400">
                  <span className="flex items-center">
                    <Sparkles className="w-4 h-4 mr-1" />
                    Confidence: 95%
                  </span>
                  <span>Language: English</span>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-purple-400">
                <div className="text-center">
                  <Mic className="w-12 h-12 mx-auto mb-3 opacity-50 animate-pulse" />
                  <p>No transcription yet. Start recording to capture voice input.</p>
                  <p className="text-xs text-purple-500 mt-2">Speak clearly for best results</p>
                </div>
              </div>
            )}
          </div>

          {/* Audio Element (hidden) */}
          <audio ref={audioRef} className="hidden" />
        </>
      )}
    </div>
  )
}

export default VoiceInterface